/**
 * CSV export helpers shared across the app (P3-5).
 *
 * Built as a Blob, never a `data:` URI: `encodeURI` leaves `#` unescaped, so a `data:` CSV was cut off at the first
 * `#` in any cell. Every cell is quoted when it needs to be and a leading formula character is neutralised.
 */

function csvCell(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  // A leading =, +, - or @ is a formula to a spreadsheet; prefix it so a name cannot run one.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsvText(rows: readonly (readonly (string | number | null | undefined)[])[]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export function downloadCsv(
  filename: string,
  rows: readonly (readonly (string | number | null | undefined)[])[],
): void {
  const url = URL.createObjectURL(new Blob([toCsvText(rows)], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** A ceiling so a mistaken unfiltered export cannot page forever: 100 pages of 100 rows. */
const MAX_EXPORT_PAGES = 100;

/**
 * Every row a paged list would show across all its pages, for an export of "what I'm filtering on", not just the
 * page on screen (P3-5). `fetchPage` returns one page's items and the total.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<{ items: readonly T[]; total: number }>,
): Promise<{ items: T[]; truncated: boolean }> {
  const items: T[] = [];
  for (let page = 1; page <= MAX_EXPORT_PAGES; page += 1) {
    const result = await fetchPage(page);
    items.push(...result.items);
    if (result.items.length === 0 || items.length >= result.total) return { items, truncated: false };
  }
  return { items, truncated: true };
}
